/**
 * Parses the route with the specified custom name and params out of the specified set of routes.
 *
 * @param routes
 * @param name
 * @param params A mapping with the key values to replace in the route url template or a single value or an array, if the correct params order is specified
 * @param locale The locale to prefix on the resolved route path.
 * @param absoluteUrl The url to prepend on the route
 * @returns {*}
 */
export function parseRoute(routes, name, params = null, locale = null, absoluteUrl = false) {
    const names = name.split('.')
    let route = routes[names[0]]

    if (!route) {
        throw new Error(`Route "${name}" is not defined.`);
    }

    for (let i = 1; i < names.length; i++) {
        route = route[names[i]]

        if (!route) {
            throw new Error(`Route "${name}" is not defined.`);
        }
    }

    // If we have an object, we get the index page
    if (typeof route === 'object') {
        route = route.index;
    }

    // Make sure we have a valid route name
    if (!route || typeof route !== 'string') {
        throw new Error(`Route "${name}" is not a valid route name.`);
    }

    if (params && Array.isArray(params)) {

        // If the params are specified as array, we just replace the route params in the specified order of the array by ignoring the names of the route params
        params.forEach((param) => {
            route = route.replace(new RegExp('{(.*?)}', 'm'), param)
        })
    } else if (params && typeof params === 'object') {

        // If the params are specified as object, we replace each item with its corresponding param key
        Object.keys(params).forEach(key => {
            route = route.replace(`{${key}}`, params[key])
        })
    } else if (params) {

        // If the params is specified as string, we just replace the one route param we expect on the route
        route = route.replace(new RegExp('{(.*?)}', 'm'), params)
    }

    // Now replace all optional route params, e.g., {param?}
    route = route.replace(new RegExp('\\/{[^}]+\\?}', 'gm'), '');

    // For server side, we have to remove the 'api' prefix for our api requests
    if (!process.client) {
        route = route.replace('/api', '')
    }

    // Remove the prefixed slash to be consistent
    route = (route.startsWith('/') ? route.substring(1) : route);

    if (absoluteUrl) {
        absoluteUrl = absoluteUrl.endsWith('/') ? absoluteUrl.slice(0, -1) : absoluteUrl;
        route = `${absoluteUrl}/` + (locale ? `${locale}/` : '') + route;

        return encodeURI(route);
    }

    return (locale ? `/${locale}/` : '/') + encodeURI(route)
}

/**
 * Parses the route with the specified vue router name and params.
 *
 * @param router
 * @param name The name generated by Vue router.
 * @param params The params object to pass to the route. This can be used to pass props to pages and templates as well.
 * @param locale The locale to use for the route name.
 * @param absoluteUrl The url to prepend on the route
 * @returns string|object
 */
export function parseVueNamedRoute(router, name, params = {}, locale = null, absoluteUrl = null) {
    let routeName = name;
    if (locale) {
        routeName = `${routeName}___${locale}`; // Use route generated by nuxt-i18n
    }

    const routeObject = {
        name: routeName,
        ...params
    }

    if (absoluteUrl) {
        const path = router.resolve(routeObject).href;
        absoluteUrl = absoluteUrl.endsWith('/') ? absoluteUrl.slice(0, -1) : absoluteUrl;

        return `${absoluteUrl}${path}`;
    }

    return routeObject
}